'use client'
import { FileIcon } from '@/components/icons/FileIcon'
import { useLocale, useTranslations } from 'next-intl'
import Link from 'next/link'
import { Tabs } from 'radix-ui'
import { useEffect, useRef } from 'react'

import { GENERAL_TABS } from '../data/tabs'

interface ICategoryTabsProps {
  selectedTab: string
  onSelectTab: (tabId: string) => void
}

export function GeneralTabs({ selectedTab, onSelectTab }: ICategoryTabsProps) {
  const t = useTranslations('General')
  const tabsListRef = useRef<HTMLDivElement>(null)
  const tabRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({})

  const scrollToTab = (tabId: string) => {
    const selectedTabElement = tabRefs.current[tabId]
    if (selectedTabElement && tabsListRef.current) {
      const tabsList = tabsListRef.current
      const tabElement = selectedTabElement

      // Calculate the position to scroll to center the tab
      const tabLeft = tabElement.offsetLeft
      const tabWidth = tabElement.offsetWidth
      const containerWidth = tabsList.offsetWidth
      const scrollPosition = tabLeft - containerWidth / 2 + tabWidth / 2

      tabsList.scrollTo({
        left: scrollPosition,
        behavior: 'smooth'
      })
    }
  }

  const locale = useLocale()

  useEffect(() => {
    const hash = window.location.hash.replace('#', '')
    if (hash && GENERAL_TABS.some((tab) => tab.id === hash)) {
      onSelectTab(hash)
      setTimeout(() => scrollToTab(hash), 100)
    }

    const handleHashChange = () => {
      const newHash = window.location.hash.replace('#', '')
      if (newHash && GENERAL_TABS.some((tab) => tab.id === newHash)) {
        onSelectTab(newHash)
        scrollToTab(newHash)
      }
    }

    window.addEventListener('hashchange', handleHashChange)

    return () => {
      window.removeEventListener('hashchange', handleHashChange)
    }
  }, [onSelectTab])

  const handleTabChange = (tabId: string) => {
    onSelectTab(tabId)
    window.history.replaceState(null, '', `#${tabId}`)
    scrollToTab(tabId)
  }

  const getUrl = (url: string | Record<string, string>) => {
    if (typeof url === 'string') {
      return url
    }

    if (typeof url === 'object') return url[locale] as string

    return url
  }

  return (
    <Tabs.Root
      className="flex w-full flex-col"
      defaultValue="legalActs"
      value={selectedTab}
      onValueChange={handleTabChange}
    >
      <Tabs.List
        ref={tabsListRef}
        className="no-scrollbar mx-auto flex w-full max-w-232.5 shrink-0 gap-4 overflow-x-scroll md:mb-6 md:justify-center md:gap-8"
        aria-label={t('title')}
      >
        {GENERAL_TABS.map((tab) => (
          <Tabs.Trigger
            key={tab.id}
            ref={(el) => {
              tabRefs.current[tab.id] = el
            }}
            className="text-primary-400 hover:text-primary-500 data-[state=active]:text-primary-500 data-[state=active]:border-primary-500 text-body-3 h-11.25 w-fit cursor-pointer items-center justify-center px-4 whitespace-nowrap ring-offset-1 transition-all ease-in data-[state=active]:border-b-2"
            value={tab.id}
          >
            {t(tab.title)}
          </Tabs.Trigger>
        ))}
      </Tabs.List>
      {GENERAL_TABS.map((tab) => (
        <Tabs.Content
          key={tab.id}
          value={tab.id}
          className="mx-auto my-4 w-full max-w-241.25 space-y-4 rounded-3xl bg-white p-4 shadow-2xl md:p-6"
        >
          <h4 className="text-body-2 text-primary-600 mb-4 font-bold">
            {tab.content?.title && t(tab.content.title)}
          </h4>

          {tab.content.links?.map((link, index) => (
            <div
              key={index}
              className="flex flex-col gap-2 rounded-xl bg-gray-50 p-2 md:p-4"
            >
              <FileIcon className="text-primary-600" />
              <Link
                target="_blank"
                rel="noopener noreferrer"
                className="text-body-3 text-primary-600 leading-5.25 underline"
                href={getUrl(link.url)}
              >
                {t(link.title)}
              </Link>
            </div>
          ))}

          {tab.content.text && (
            <p className="text-body-3 text-primary-600 leading-5.25">
              {t(tab.content.text)}
            </p>
          )}
        </Tabs.Content>
      ))}
    </Tabs.Root>
  )
}
